import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import PolicyRounded from '@mui/icons-material/PolicyRounded';
import HubRounded from '@mui/icons-material/HubRounded';
import NotificationsRounded from '@mui/icons-material/NotificationsRounded';
import QueryStatsRounded from '@mui/icons-material/QueryStatsRounded';
import AssessmentRounded from '@mui/icons-material/AssessmentRounded';
import TrendingUpRounded from '@mui/icons-material/TrendingUpRounded';
import FolderSharedRounded from '@mui/icons-material/FolderSharedRounded';
import StorageRounded from '@mui/icons-material/StorageRounded';
import CloudQueueRounded from '@mui/icons-material/CloudQueueRounded';
import { VerifiedUserIcon, LockIcon } from '../../shared/icons';
import {
  Section,
  Container,
  AnimateOnScroll,
  CenteredHeader,
  SectionEyebrow,
  SectionTitle,
  CapabilitiesBento,
  BentoCard,
  BentoIcon,
  BentoTitle,
  BentoTitleRow,
  BentoUpcomingBadge,
  BentoDesc,
  BentoTags,
  BentoTag,
  CapVisualFrame,
  CitationTrace,
  CitationQuote,
  CitationChip,
  CitationConnector,
  CitationSource,
  CitationSourceIcon,
  CitationSourceBody,
  CitationSourceTitle,
  CitationSourceMeta,
  CitationSourceHighlight,
  GovCompare,
  GovPanel,
  GovPanelLabel,
  GovHeadline,
  GovSwatchRow,
  GovSwatch,
  GovArrow,
  GovChecklist,
  GovCheck,
  VaultStack,
  VaultCard,
  VaultLock,
  VaultBody,
  VaultName,
  VaultMeta,
  VaultIsolationLine,
  VaultBadges,
  VaultBadge,
  ConnectorOrbit,
  ConnectorOrbitChip,
  ConnectorOrbitHub,
  ConnectorOrbitHubLabel,
  ConnectorFootnote,
} from '../home/HomePage.styles';

// =============================================================================
// SHARED PRODUCT-PAGE KIT (/product, /excel, /word).
//
// Default export: the CORE CAPABILITIES bento, with a small set of app-aware
// copy substitutions driven by the `app` prop.
//
// Named exports: the two pieces of page furniture all three product pages need
// to render identically, so the surfaces cannot drift apart:
//   - CtaRow: the single primary action plus the Microsoft Store credential.
//   - FaqList: the FAQ accordion, fed from FAQ_REPOSITORY by the calling page.
// =============================================================================

// Brand constants used by the shared furniture below. The rust accent and the
// ink text color are site-wide; per-app accents only recolor the bento.
const BRAND_ACCENT = '#B83B1D';
const BRAND_INK = '#2E140A';
const MICROSOFT_STORE_URL = 'https://marketplace.microsoft.com/en-us/product/WA200010426';

// -----------------------------------------------------------------------------
// Per-app ACCENT theming.
//
// The shared HomePage.styles.js components hardcode the PowerPoint rust accent
// (#B83B1D, and its rgba(184, 59, 29, a) / alpha() variants) directly in JS, so
// a wrapper CSS variable cannot reach them. To recolor ONLY this component
// instance without touching the shared styles, we override the exact rust-bearing
// CSS properties via the `sx` prop, recomputing each alpha level against the
// app's accent RGB. PowerPoint resolves to the same rust values as today.
//
// Only the rust accent moves. Semantic success greens (#16a34a vault dot), the
// "Generic AI" blue swatches, the dark hub gradient, and the neutral category
// chrome are left untouched.
// -----------------------------------------------------------------------------
const APP_ACCENT = {
  powerpoint: { hex: '#B83B1D', rgb: '184, 59, 29' },
  excel: { hex: '#217346', rgb: '33, 115, 70' },
  word: { hex: '#35548A', rgb: '53, 84, 138' },
};

// Returns an accent helper for the active app: the solid hex plus an `a(x)`
// helper that yields the app accent at a given alpha, matching the alpha levels
// the shared styled components apply to the rust accent.
function getAccent(app) {
  const { hex, rgb } = APP_ACCENT[app] || APP_ACCENT.powerpoint;
  const a = (alpha) => `rgba(${rgb}, ${alpha})`;
  return { hex, a };
}

// Per-app copy that differs between PowerPoint, Excel, and Word. Everything
// else in the bento is identical across the three surfaces.
const APP_COPY = {
  powerpoint: {
    citationLocation: 'hoverable in the deck',
    governanceDesc:
      'Restack reads your slide master and holds it: fonts, palette, margins, disclaimers. Client-ready, not a first draft you reformat.',
    nativeTag: 'Native PPTX',
  },
  excel: {
    citationLocation: 'linked in the cell',
    governanceDesc:
      'Restack reads your workbook template and enforces your house style: fonts, palette, margins, disclaimers. Client-ready, not a first draft you reformat.',
    nativeTag: 'Native XLSX',
  },
  word: {
    citationLocation: 'linked in the paragraph',
    governanceDesc:
      'Restack reads your document template and enforces your house style: fonts, palette, margins, disclaimers. Client-ready, not a first draft you reformat.',
    nativeTag: 'Native DOCX',
  },
};

function buildCapabilities(app) {
  const copy = APP_COPY[app] || APP_COPY.powerpoint;
  return [
    {
      icon: <VerifiedUserIcon />,
      title: 'Audit-ready citations',
      desc: `Claims trace to the exact page and paragraph. Defensible in committee, ${copy.citationLocation}.`,
      tags: [app === 'powerpoint' ? 'Every Claim Cited' : 'Claims Cited', 'Source Trails'],
      variant: 'purple',
      visual: 'citations',
    },
    {
      icon: <PolicyRounded />,
      title: "Holds your firm's template",
      desc: copy.governanceDesc,
      tags: ['Brand Enforcement', copy.nativeTag],
      variant: 'cyan',
      visual: 'governance',
    },
    // The chart-grammar and QA-pass cards were removed: both capabilities
    // already carry animated rows in the How-it-works showcase, and
    // repeating them here diluted the grid.
    {
      icon: <LockIcon />,
      title: 'The private knowledge locker',
      desc: 'Sealed per engagement. No cross-project leakage. No training on your data.',
      tags: ['Single-Tenant', 'Zero Training'],
      variant: 'green',
      visual: 'locker',
    },
    {
      icon: <HubRounded />,
      title: 'Your data stack, connected',
      desc: 'Plugs into market terminals, document stores, and the open web, all under your firm’s credentials.',
      tags: ['BYOC Credentials'],
      variant: 'orange',
      visual: 'connectors',
    },
  ];
}

export default function CoreCapabilities({ app = 'powerpoint', isVisible = () => true }) {
  const capabilities = buildCapabilities(app);
  const accent = getAccent(app);

  // --- Per-app accent sx overrides for the shared styled components. Each maps
  // the rust property the component hardcodes onto the active app accent at the
  // same alpha. PowerPoint reproduces today's rust exactly.
  const bentoIconSx = {
    background: `linear-gradient(135deg, ${accent.a(0.12)} 0%, ${accent.a(0.12)} 100%)`,
    '& svg': { color: accent.hex },
    '.MuiBox-root:hover &': { background: accent.a(0.18) },
  };
  const upcomingBadgeSx = {
    color: accent.hex,
    background: accent.a(0.08),
    border: `1px dashed ${accent.a(0.35)}`,
    '& svg': { color: accent.hex },
  };

  // Citations visual
  const citationQuoteSx = { '&::before': { color: accent.a(0.35) } };
  const citationChipSx = {
    background: accent.a(0.12),
    color: accent.hex,
    border: `1px solid ${accent.a(0.25)}`,
    '&:hover': { background: accent.a(0.2) },
  };
  const citationConnectorSx = {
    background: `linear-gradient(180deg, ${accent.a(0.4)}, ${accent.a(0)})`,
    '&::after': {
      borderRight: `1.5px solid ${accent.a(0.5)}`,
      borderBottom: `1.5px solid ${accent.a(0.5)}`,
    },
  };
  const citationSourceSx = { border: `1px solid ${accent.a(0.2)}` };
  const citationSourceIconSx = {
    background: accent.a(0.08),
    border: `1px solid ${accent.a(0.2)}`,
    '&::before': { background: accent.a(0.18) },
    '&::after': { color: accent.hex },
  };
  const citationSourceHighlightSx = {
    background: `linear-gradient(90deg, ${accent.a(0.35)} 0%, ${accent.a(0.15)} 100%)`,
    '&::after': { background: accent.a(0.55) },
  };

  // Governance visual (only the "good"/Restack side is rust; the "bad" side and
  // its blue swatches stay as-is)
  const govPanelGoodSx = {
    border: `1px solid ${accent.a(0.3)}`,
    boxShadow: `0 0 0 3px ${accent.a(0.06)}`,
  };
  const govPanelLabelGoodSx = { color: accent.hex };
  const govArrowSx = { color: accent.a(0.5) };
  const govCheckSx = {
    '&::before': {
      background: accent.a(0.12),
      border: `1px solid ${accent.a(0.3)}`,
      backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' fill='none'><path d='M3 6.5L5 8.5L9 4' stroke='${encodeURIComponent(accent.hex)}' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>")`,
    },
  };
  // The "good" brand swatch trio: keep PowerPoint's exact rust ramp; for Excel
  // and Word derive a matching tint ramp from the app accent.
  const GOOD_SWATCHES = {
    powerpoint: ['#B83B1D', '#D4694E', '#E8B8A4'],
    excel: ['#217346', '#4E9D74', '#A4D4BC'],
    word: ['#35548A', '#5E7CB0', '#B0C2DD'],
  };
  const goodSwatches = GOOD_SWATCHES[app] || GOOD_SWATCHES.powerpoint;

  // Locker visual
  const vaultCardSx = { '&:hover': { borderColor: accent.a(0.3) } };
  const vaultLockSx = {
    background: accent.a(0.1),
    border: `1px solid ${accent.a(0.22)}`,
    '& svg': { color: accent.hex },
  };
  const vaultBadgeSx = {
    background: accent.a(0.08),
    color: accent.hex,
    border: `1px solid ${accent.a(0.2)}`,
  };

  // Connectors visual
  const orbitChipSx = {
    '& svg': { color: accent.hex },
    '&:hover': { borderColor: accent.a(0.35) },
  };
  const orbitHubSx = { '&::after': { border: `1px solid ${accent.a(0.35)}` } };

  // Locker + connectors visuals keep the per-app accent recoloring above.

  return (
    <Section id="capabilities">
      <Container>
        <AnimateOnScroll isVisible={isVisible('core-capabilities')} data-animate-id="core-capabilities">
          <CenteredHeader>
            <SectionEyebrow>Core Capabilities</SectionEyebrow>
            <SectionTitle component="h2">What <em>committee-grade work</em> requires.</SectionTitle>
          </CenteredHeader>
        </AnimateOnScroll>

        <CapabilitiesBento>
          {capabilities.map((cap, i) => (
            <AnimateOnScroll
              key={i}
              isVisible={isVisible(`bento-${i}`)}
              delay={0.1 * ((i % 2) + 1)}
              data-animate-id={`bento-${i}`}
            >
              <BentoCard>
                <BentoIcon variant={cap.variant} sx={bentoIconSx}>{cap.icon}</BentoIcon>
                <BentoTitleRow>
                  <BentoTitle component="h3">{cap.title}</BentoTitle>
                  {cap.upcoming && (
                    <BentoUpcomingBadge sx={upcomingBadgeSx}>
                      <NotificationsRounded />
                      Rolling out next
                    </BentoUpcomingBadge>
                  )}
                </BentoTitleRow>
                <BentoDesc>{cap.desc}</BentoDesc>

                {cap.tags && (
                  <BentoTags>
                    {cap.tags.map((tag, j) => (
                      <BentoTag key={j}>{tag}</BentoTag>
                    ))}
                  </BentoTags>
                )}

                {cap.visual === 'citations' && (
                  <CapVisualFrame>
                    <CitationTrace>
                      <CitationQuote sx={citationQuoteSx}>
                        &nbsp;&nbsp;Nordic fintech TAM grew 23% YoY to &euro;4.2B in 2026
                        <CitationChip sx={citationChipSx}>1</CitationChip>
                        <CitationChip sx={citationChipSx}>2</CitationChip>
                      </CitationQuote>
                      <CitationConnector sx={citationConnectorSx} />
                      <CitationSource sx={citationSourceSx}>
                        <CitationSourceIcon sx={citationSourceIconSx} />
                        <CitationSourceBody>
                          <CitationSourceTitle>ECB Fintech Report Q3 2026</CitationSourceTitle>
                          <CitationSourceMeta>Page 14 &middot; Paragraph 3</CitationSourceMeta>
                          <CitationSourceHighlight sx={citationSourceHighlightSx} />
                        </CitationSourceBody>
                      </CitationSource>
                    </CitationTrace>
                  </CapVisualFrame>
                )}

                {cap.visual === 'governance' && (
                  <CapVisualFrame>
                    <GovCompare>
                      <GovPanel variant="bad">
                        <GovPanelLabel variant="bad">Generic AI</GovPanelLabel>
                        <GovHeadline variant="bad">Market Entry Strategy</GovHeadline>
                        <GovSwatchRow>
                          <GovSwatch color="#3B82F6" />
                          <GovSwatch color="#93C5FD" />
                          <GovSwatch color="#BFDBFE" />
                        </GovSwatchRow>
                      </GovPanel>
                      <GovArrow className="gov-arrow" sx={govArrowSx}>&rarr;</GovArrow>
                      <GovPanel variant="good" sx={govPanelGoodSx}>
                        <GovPanelLabel variant="good" sx={govPanelLabelGoodSx}>Restack</GovPanelLabel>
                        <GovHeadline variant="good">Market Entry Strategy</GovHeadline>
                        <GovSwatchRow>
                          <GovSwatch color={goodSwatches[0]} />
                          <GovSwatch color={goodSwatches[1]} />
                          <GovSwatch color={goodSwatches[2]} />
                        </GovSwatchRow>
                      </GovPanel>
                    </GovCompare>
                    <GovChecklist>
                      <GovCheck sx={govCheckSx}>Typography</GovCheck>
                      <GovCheck sx={govCheckSx}>Palette</GovCheck>
                      <GovCheck sx={govCheckSx}>Margins</GovCheck>
                      <GovCheck sx={govCheckSx}>Disclaimers</GovCheck>
                    </GovChecklist>
                  </CapVisualFrame>
                )}

                {cap.visual === 'locker' && (
                  <CapVisualFrame>
                    <VaultStack>
                      <VaultCard index={0} sx={vaultCardSx}>
                        <VaultLock sx={vaultLockSx}><LockIcon /></VaultLock>
                        <VaultBody>
                          <VaultName>Project Atlas</VaultName>
                          <VaultMeta>Sealed &middot; Client A</VaultMeta>
                        </VaultBody>
                      </VaultCard>
                      <VaultIsolationLine />
                      <VaultCard index={1} sx={vaultCardSx}>
                        <VaultLock sx={vaultLockSx}><LockIcon /></VaultLock>
                        <VaultBody>
                          <VaultName>Project Nova</VaultName>
                          <VaultMeta>Sealed &middot; Client B</VaultMeta>
                        </VaultBody>
                      </VaultCard>
                      <VaultIsolationLine />
                      <VaultCard index={2} sx={vaultCardSx}>
                        <VaultLock sx={vaultLockSx}><LockIcon /></VaultLock>
                        <VaultBody>
                          <VaultName>Project Vega</VaultName>
                          <VaultMeta>Sealed &middot; Client C</VaultMeta>
                        </VaultBody>
                      </VaultCard>
                    </VaultStack>
                    <VaultBadges>
                      <VaultBadge sx={vaultBadgeSx}>Single-Tenant</VaultBadge>
                      <VaultBadge sx={vaultBadgeSx}>Zero Training</VaultBadge>
                      <VaultBadge sx={vaultBadgeSx}>Purge on Close</VaultBadge>
                    </VaultBadges>
                  </CapVisualFrame>
                )}

                {cap.visual === 'connectors' && (
                  <CapVisualFrame>
                    <ConnectorOrbit>
                      {[
                        { name: 'Bloomberg', icon: <QueryStatsRounded />, angle: -135 },
                        { name: 'Capital IQ', icon: <AssessmentRounded />, angle: -90 },
                        { name: 'PitchBook', icon: <TrendingUpRounded />, angle: -45 },
                        { name: 'SharePoint', icon: <FolderSharedRounded />, angle: 0 },
                        { name: 'Box', icon: <StorageRounded />, angle: 45 },
                        { name: 'OneDrive', icon: <CloudQueueRounded />, angle: 90 },
                        { name: 'SEC EDGAR', icon: <PolicyRounded />, angle: 135 },
                      ].map(({ name, icon, angle }) => (
                        <ConnectorOrbitChip key={name} angle={angle} sx={orbitChipSx}>
                          {icon}
                          {name}
                        </ConnectorOrbitChip>
                      ))}
                      <ConnectorOrbitHub sx={orbitHubSx}>
                        <HubRounded />
                      </ConnectorOrbitHub>
                      <ConnectorOrbitHubLabel>Restack</ConnectorOrbitHubLabel>
                    </ConnectorOrbit>
                    <ConnectorFootnote>
                      Uses your firm&rsquo;s credentials &middot; more on request
                    </ConnectorFootnote>
                  </CapVisualFrame>
                )}
              </BentoCard>
            </AnimateOnScroll>
          ))}
        </CapabilitiesBento>
      </Container>
    </Section>
  );
}

// =============================================================================
// SHARED PAGE FURNITURE
// =============================================================================

/**
 * CtaRow: ONE primary action (solid, to /demo) plus the Microsoft Store listing
 * beside it as the proof signal. Every product page renders this twice, in the
 * hero and in the closing band, with the SAME `label` both times, so the page
 * only ever asks the reader for one thing.
 *
 * The Store link is the only proof claim on these pages, and it is verifiable:
 * it points at the live marketplace listing.
 */
export function CtaRow({ label, theme, sx }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1.25,
        ...sx,
      }}
    >
      <Box
        component={RouterLink}
        to="/demo"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.8,
          px: { xs: 2.5, md: 3.25 },
          height: 48,
          borderRadius: '999px',
          fontFamily: theme.typography.fontFamily,
          fontWeight: 600,
          fontSize: { xs: '0.85rem', md: '0.9rem' },
          letterSpacing: '-0.005em',
          whiteSpace: 'nowrap',
          color: '#FAF9F7',
          backgroundColor: BRAND_INK,
          border: '1px solid transparent',
          textDecoration: 'none',
          cursor: 'pointer',
          transition: 'background-color 0.2s ease, box-shadow 0.2s ease, transform 0.12s ease',
          '&:hover': { backgroundColor: '#1A0A04', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.22)' },
          '&:active': { transform: 'translateY(1px)', boxShadow: 'none' },
          '&:focus-visible': { outline: `2px solid ${BRAND_ACCENT}`, outlineOffset: 3 },
        }}
      >
        {label}
        <ArrowForwardRounded aria-hidden="true" sx={{ fontSize: '1.05rem' }} />
      </Box>

      <Box
        component="a"
        href={MICROSOFT_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Restack on the Microsoft Store, opens in a new tab"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          px: { xs: 2, md: 2.75 },
          height: 48,
          borderRadius: '999px',
          fontFamily: theme.typography.fontFamily,
          fontWeight: 500,
          fontSize: { xs: '0.85rem', md: '0.9rem' },
          letterSpacing: '-0.005em',
          whiteSpace: 'nowrap',
          color: 'text.primary',
          backgroundColor: '#f4f1ed',
          border: `1px solid ${alpha(BRAND_INK, 0.16)}`,
          textDecoration: 'none',
          cursor: 'pointer',
          transition: 'background-color 0.2s ease, border-color 0.2s ease, transform 0.12s ease',
          '&:hover': { backgroundColor: '#ede8e1', borderColor: alpha(BRAND_INK, 0.28) },
          '&:active': { transform: 'translateY(1px)' },
          '&:focus-visible': { outline: `2px solid ${BRAND_ACCENT}`, outlineOffset: 3 },
        }}
      >
        {/* Plain svg, not Box: MUI's system claims `width`/`height` as style
            props, so string values emit unitless CSS the browser drops and the
            mark never receives the attributes. It then fills its container.
            Matches the markup HomePage and AudiencePage already use. */}
        <svg width="16" height="16" viewBox="0 0 23 23" aria-hidden="true" style={{ flexShrink: 0 }}>
          <rect x="1" y="1" width="10" height="10" fill="#F25022" />
          <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
          <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
          <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
        </svg>
        Live on Microsoft Store
      </Box>
    </Box>
  );
}

/**
 * WorkflowContrast: the problem-to-solution block. Each row is one step of the
 * recurring deliverable cycle, shown as it runs today next to what changes with
 * Restack in the file.
 *
 * `rows` are `{ step, before, after }`. The column headers are decorative on
 * desktop (each card repeats its own label on mobile, where the columns stack),
 * so the desktop header row is aria-hidden rather than read twice.
 */
export function WorkflowContrast({ rows, accent, theme, hair, isVisible, idPrefix = 'rebuild' }) {
  const columnLabelSx = {
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.68rem',
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: alpha(BRAND_INK, 0.45),
  };
  const bodySx = {
    fontFamily: theme.typography.fontFamily,
    fontSize: '0.84rem',
    lineHeight: 1.55,
  };

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 2.5 } }}>
      <Box
        aria-hidden="true"
        sx={{ display: { xs: 'none', md: 'grid' }, gridTemplateColumns: '132px 1fr 1fr', gap: 2.5, px: 0.5 }}
      >
        <Box />
        <Typography sx={columnLabelSx}>Today</Typography>
        <Typography sx={{ ...columnLabelSx, color: accent }}>With Restack</Typography>
      </Box>

      {rows.map((row, i) => (
        <AnimateOnScroll
          key={row.step}
          isVisible={isVisible(`${idPrefix}-${i}`)}
          delay={0.06 * (i + 1)}
          data-animate-id={`${idPrefix}-${i}`}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '132px 1fr 1fr' },
              gap: { xs: 1.5, md: 2.5 },
              alignItems: 'stretch',
            }}
          >
            <Typography
              component="h3"
              sx={{
                fontFamily: theme.typography.fontFamily,
                fontSize: '0.95rem',
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: 'text.primary',
                alignSelf: 'center',
              }}
            >
              {row.step}
            </Typography>

            <Box sx={{ p: { xs: 2.5, md: 3 }, height: '100%', borderRadius: 2, border: `1px solid ${hair}`, backgroundColor: alpha(BRAND_INK, 0.015) }}>
              <Typography sx={{ ...columnLabelSx, display: { md: 'none' }, mb: 0.75 }}>Today</Typography>
              <Typography sx={{ ...bodySx, color: 'text.secondary' }}>{row.before}</Typography>
            </Box>

            <Box sx={{ p: { xs: 2.5, md: 3 }, height: '100%', borderRadius: 2, border: `1px solid ${alpha(accent, 0.28)}`, backgroundColor: alpha(accent, 0.05) }}>
              <Typography sx={{ ...columnLabelSx, display: { md: 'none' }, color: accent, mb: 0.75 }}>With Restack</Typography>
              <Typography sx={{ ...bodySx, color: 'text.primary' }}>{row.after}</Typography>
            </Box>
          </Box>
        </AnimateOnScroll>
      ))}
    </Box>
  );
}

/**
 * SurfaceLink: an inline text link to a sibling product surface. Cross-surface
 * navigation is deliberately NOT a button: a second button next to the primary
 * CTA reads as a competing primary action.
 */
export function SurfaceLink({ to, children }) {
  return (
    <Box
      component={RouterLink}
      to={to}
      sx={{
        color: BRAND_ACCENT,
        fontWeight: 600,
        textDecoration: 'none',
        borderBottom: `1px solid ${alpha(BRAND_ACCENT, 0.35)}`,
        transition: 'border-color 0.2s ease, color 0.2s ease',
        '&:hover': { borderBottomColor: BRAND_ACCENT },
        '&:active': { color: '#9A3018' },
        '&:focus-visible': { outline: `2px solid ${BRAND_ACCENT}`, outlineOffset: 2, borderRadius: '2px' },
      }}
    >
      {children}
    </Box>
  );
}

/**
 * FaqList: the FAQ accordion. `entries` comes from FAQ_REPOSITORY (looked up by
 * id on the calling page) so the page and the support widget serve identical
 * answers.
 *
 * MUI v7 already renders its own <h3> around the AccordionSummary, so the
 * question Typography must stay a <span> or the heading nests inside a heading.
 */
export function FaqList({ entries, theme }) {
  return (
    <Box sx={{ maxWidth: 760, mx: 'auto' }}>
      {entries.map(({ question, answer }) => (
        <Accordion
          key={question}
          disableGutters
          elevation={0}
          square
          sx={{
            backgroundColor: 'transparent',
            borderBottom: `1px solid ${alpha(BRAND_INK, 0.08)}`,
            '&::before': { display: 'none' },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreRounded sx={{ color: alpha(BRAND_INK, 0.45) }} />}
            sx={{
              px: 0.5,
              minHeight: 52,
              '& .MuiAccordionSummary-content': { my: 1 },
              '&:hover': { backgroundColor: alpha(BRAND_INK, 0.02) },
              '&.Mui-focusVisible': {
                backgroundColor: 'transparent',
                outline: `2px solid ${BRAND_ACCENT}`,
                outlineOffset: -2,
              },
            }}
          >
            <Typography
              component="span"
              sx={{
                fontFamily: theme.typography.fontFamily,
                fontSize: '0.92rem',
                fontWeight: 600,
                letterSpacing: '-0.01em',
                color: 'text.primary',
              }}
            >
              {question}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0.5, pt: 0, pb: 2 }}>
            <Typography
              sx={{
                fontFamily: theme.typography.fontFamily,
                fontSize: '0.84rem',
                lineHeight: 1.6,
                color: 'text.secondary',
              }}
            >
              {answer}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}
